import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Checkbox, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import EquipmentLabelDialog from './EquipmentLabelDialog';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { DepartmentService } from '../../services/department.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { useEquipmentGroups } from './useEquipmentGroups';
import {
    EQUIPMENT_STATUSES, EquipmentCategory, EquipmentDTO, EquipmentFilters,
    EquipmentStatus, EquipmentTypeDTO, mntLabel,
} from '../../interfaces/maintenance';
import { Department } from '../../interfaces/department.interface';

export const STATUS_COLOR: Record<EquipmentStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    RUNNING: 'success', IDLE: 'default', DOWN: 'error', UNDER_MAINTENANCE: 'warning', RETIRED: 'default',
};

const cell = (basis: number) => ({ flex: `1 1 ${basis}px`, maxWidth: basis + 140, minWidth: 0 });

/** Equipment register (maintenance L0): searchable, filterable, paged. */
const EquipmentList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_EQUIPMENT_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<EquipmentDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [q, setQ] = useState('');
    const [typeId, setTypeId] = useState('');
    const [category, setCategory] = useState<EquipmentCategory | ''>('');
    const [status, setStatus] = useState<EquipmentStatus | ''>('');
    const [departmentId, setDepartmentId] = useState('');
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const { groups } = useEquipmentGroups(institutionId);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [labelOpen, setLabelOpen] = useState(false);
    const toggle = (rid: string) => setSelected(sel => sel.includes(rid) ? sel.filter(x => x !== rid) : [...sel, rid]);
    const pageIds = rows.map(r => r.id);
    const allOnPage = pageIds.length > 0 && pageIds.every(i => selected.includes(i));

    useEffect(() => {
        MaintenanceService.listTypes(institutionId).then(setTypes).catch(e => toast.showApiError(e, 'Failed to load equipment types'));
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        const filters: EquipmentFilters = { q: q.trim(), typeId, category, status, departmentId };
        try {
            setLoading(true);
            const res = await MaintenanceService.listEquipment(institutionId, filters, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load equipment');
        } finally {
            setLoading(false);
        }
    }, [institutionId, q, typeId, category, status, departmentId, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const resetPage = () => setPage(0);

    const handleExport = async () => {
        try {
            const blob = await MaintenanceService.exportEquipmentCsv(institutionId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'equipment.csv';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.showApiError(e, 'Failed to export equipment');
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.equipment.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Equipment</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button data-help="maint.equipment.export" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>
                    {canManage && (
                        <Button data-help="maint.equipment.printLabels" startIcon={<QrCode2Icon />} disabled={selected.length === 0}
                            onClick={() => setLabelOpen(true)}>
                            Print labels{selected.length > 0 ? ` (${selected.length})` : ''}
                        </Button>
                    )}
                    {canManage && (
                        <Button data-help="maint.equipment.import" startIcon={<UploadFileIcon />} onClick={() => navigate('/maintenance/equipment/import')}>Import CSV</Button>
                    )}
                    {canManage && (
                        <Button data-help="maint.equipment.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/maintenance/equipment/new')}>
                            New equipment
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="maint.equipment.filter">
                <TextField
                    size="small" label="Search code, name, serial" data-help="maint.equipment.filter.search" value={q} sx={cell(220)}
                    onChange={e => { setQ(e.target.value); resetPage(); }}
                />
                <Box sx={cell(200)}>
                    <SearchableSelect
                        options={types} value={typeId} size="small" label="Type" data-help="maint.equipment.filter.type"
                        getOptionLabel={t => `${t.name} (${t.code})`}
                        onChange={id => { setTypeId(id); resetPage(); }}
                    />
                </Box>
                <Box sx={cell(180)}>
                    <SearchableSelect
                        options={groups} value={category} size="small" label="Group" data-help="maint.equipment.filter.category"
                        getOptionId={g => g.code} getOptionLabel={g => g.name}
                        onChange={code => { setCategory(code as EquipmentCategory | ''); resetPage(); }}
                    />
                </Box>
                <TextField
                    select size="small" label="Status" data-help="maint.equipment.filter.status" value={status} sx={cell(150)}
                    onChange={e => { setStatus(e.target.value as EquipmentStatus | ''); resetPage(); }}
                >
                    <MenuItem value="">All</MenuItem>
                    {EQUIPMENT_STATUSES.map(s => <MenuItem key={s} value={s}>{mntLabel(s)}</MenuItem>)}
                </TextField>
                <Box sx={cell(200)}>
                    <SearchableSelect
                        options={departments} value={departmentId} size="small" label="Location" data-help="maint.equipment.filter.location"
                        getOptionLabel={d => d.name}
                        onChange={id => { setDepartmentId(id); resetPage(); }}
                    />
                </Box>
            </Box>

            <Paper data-help-section="maint.equipment.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {canManage && (
                                        <TableCell padding="checkbox" data-help="maint.equipment.col.select">
                                            <Checkbox size="small" checked={allOnPage} inputProps={{ 'aria-label': 'Select all machines on this page' }}
                                                onChange={() => setSelected(sel => allOnPage ? sel.filter(i => !pageIds.includes(i)) : Array.from(new Set([...sel, ...pageIds])))} />
                                        </TableCell>
                                    )}
                                    <TableCell data-help="maint.equipment.col.code">Code</TableCell>
                                    <TableCell data-help="maint.equipment.col.name">Name</TableCell>
                                    <TableCell data-help="maint.equipment.col.type">Type</TableCell>
                                    <TableCell data-help="maint.equipment.col.location">Location</TableCell>
                                    <TableCell data-help="maint.equipment.col.crit">Criticality</TableCell>
                                    <TableCell data-help="maint.equipment.col.status">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">No equipment found</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow
                                        key={r.id} hover sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/maintenance/equipment/${r.id}`)}
                                    >
                                        {canManage && (
                                            <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                                <Checkbox data-help="maint.equipment.action.select" size="small" checked={selected.includes(r.id)} onChange={() => toggle(r.id)}
                                                    inputProps={{ 'aria-label': `Select ${r.code}` }} />
                                            </TableCell>
                                        )}
                                        <TableCell>{r.code}</TableCell>
                                        <TableCell>{r.name}</TableCell>
                                        <TableCell>{r.equipmentTypeName}</TableCell>
                                        <TableCell>{r.locationDepartmentName ?? '-'}</TableCell>
                                        <TableCell>{r.criticality}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={mntLabel(r.status)} color={STATUS_COLOR[r.status]} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={size}
                    rowsPerPageOptions={[10, 20, 50]}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>
            <EquipmentLabelDialog open={labelOpen} equipmentIds={selected} onClose={() => setLabelOpen(false)} />
        </Box>
    );
};

export default EquipmentList;
